/**
 * Stable data-set identity for collection execution results.
 * Labels are derived from configured display order (sorted index), not DB id or stale names.
 */

import { sortNamedDataSets } from './sortDataSets';
import type { CollectionExecutionReportDataSetRef } from './collectionExecutionReport';

export type ExecutionDataSetIdentity = {
  dataSetRoundIndex: number;
  dataSetOrder: number;
  dataSetLabel: string;
  dataSet: CollectionExecutionReportDataSetRef | null;
};

/** Standard auto-generated names like "Data Set 3" can be stale after reordering. */
export function isAutoGeneratedDataSetName(name: string | null | undefined): boolean {
  if (!name) return false;
  return /^Data Set\s+\d+/i.test(name.trim());
}

/**
 * Bind execution metadata before running a request with this data set.
 * `dataSetRoundIndex` is the 0-based index in the sorted execution list.
 */
export function buildExecutionDataSetIdentity(
  dataSet: { id?: number | null; name?: string | null } | null | undefined,
  dataSetRoundIndex: number
): ExecutionDataSetIdentity {
  const order = Math.max(1, Math.floor(dataSetRoundIndex) + 1);
  const label = `Data Set ${order}`;
  const id = dataSet?.id != null ? Number(dataSet.id) : NaN;
  return {
    dataSetRoundIndex,
    dataSetOrder: order,
    dataSetLabel: label,
    dataSet: Number.isFinite(id) && id > 0 ? { id, name: label } : null,
  };
}

export { sortNamedDataSets };
